/** @format */

import React, { useState } from "react";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Box, TextField, Typography, CircularProgress } from "@mui/material";
import { LockOutlined } from "@mui/icons-material";
import { colors } from "../../../utility/types/constant";
import {
    CollectPrimaryButton,
    CollectInfoBanner,
} from "../styles";
import { ICollectSubscriptionResult } from "../../../utility/types/collectTypes";

// ─────────────────────────────────────────────────────────────────────────
// Reusable Stripe card form for Collect billing (Story 2).
//
// The whole app is already wrapped in <Elements> (App.tsx), so this component
// just consumes the context. Flow:
//   1. stripe.createPaymentMethod(card) → paymentMethodId
//   2. onCreatePaymentMethod(paymentMethodId) → backend creates/resubscribes
//      and returns { requiresAction, clientSecret }
//   3. If requiresAction, run stripe.confirmCardPayment(clientSecret) to
//      satisfy SCA / 3-D Secure, then onSuccess().
//
// All Stripe-side errors (declined card, failed challenge) are surfaced
// inline so the member can correct and retry without losing the page.
// ─────────────────────────────────────────────────────────────────────────

interface CollectPaymentFormProps {
    submitLabel: string;
    /** Dispatches the create/resubscribe call and returns the SCA hint. */
    onCreatePaymentMethod: (
        paymentMethodId: string,
    ) => Promise<ICollectSubscriptionResult>;
    /** Called after the charge (and any SCA challenge) fully succeeds. */
    onSuccess: () => void;
    /** Optional cardholder name prefill (e.g. business name). */
    defaultCardholderName?: string;
    /** External busy flag (e.g. store isSubmitting) to disable the form. */
    busy?: boolean;
}

const CARD_ELEMENT_OPTIONS = {
    hidePostalCode: false,
    style: {
        base: {
            fontFamily: "'Montserrat', sans-serif",
            fontSize: "16px",
            color: "#1f2937",
            "::placeholder": { color: "#9ca3af" },
        },
        invalid: { color: "#b91c1c" },
    },
};

const CollectPaymentForm: React.FC<CollectPaymentFormProps> = ({
    submitLabel,
    onCreatePaymentMethod,
    onSuccess,
    defaultCardholderName,
    busy,
}) => {
    const stripe = useStripe();
    const elements = useElements();

    const [cardholderName, setCardholderName] = useState(
        defaultCardholderName || "",
    );
    const [cardComplete, setCardComplete] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const disabled = !stripe || !elements || processing || !!busy;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!stripe || !elements) {
            setError("Payment form is still loading. Please try again.");
            return;
        }
        if (!cardholderName.trim()) {
            setError("Please enter the cardholder's name.");
            return;
        }

        const cardElement = elements.getElement(CardElement);
        if (!cardElement) {
            setError("Card details are incomplete. Please re-enter them.");
            return;
        }

        setProcessing(true);
        try {
            // 1. Tokenize the card into a PaymentMethod.
            const { error: pmError, paymentMethod } =
                await stripe.createPaymentMethod({
                    type: "card",
                    card: cardElement,
                    billing_details: { name: cardholderName.trim() },
                });

            if (pmError) {
                throw new Error(
                    pmError.message ||
                    "We couldn't validate that card. Please check the details.",
                );
            }
            if (!paymentMethod?.id) {
                throw new Error("We couldn't validate that card.");
            }

            // 2. Create/resubscribe on the backend.
            const result = await onCreatePaymentMethod(paymentMethod.id);

            // 3. Handle Strong Customer Authentication if required.
            if (result.requiresAction && result.clientSecret) {
                const { error: confirmError } =
                    await stripe.confirmCardPayment(result.clientSecret);
                if (confirmError) {
                    throw new Error(
                        confirmError.message ||
                        "We couldn't confirm that payment. Please try a different card.",
                    );
                }
            }

            onSuccess();
        } catch (err: any) {
            setError(
                err?.message ||
                "Something went wrong processing your payment. Please try again.",
            );
        } finally {
            setProcessing(false);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
            {error && (
                <CollectInfoBanner tone="warn" role="alert">
                    {error}
                </CollectInfoBanner>
            )}

            <TextField
                label="Cardholder name"
                value={cardholderName}
                onChange={(e) => setCardholderName(e.target.value)}
                fullWidth
                size="small"
                autoComplete="cc-name"
                sx={{ mb: 2 }}
            />

            <Box
                sx={{
                    border: "1px solid rgba(15, 23, 42, 0.15)",
                    borderRadius: "10px",
                    padding: "14px 16px",
                    backgroundColor: "#ffffff",
                    mb: 2,
                }}
            >
                <CardElement
                    options={CARD_ELEMENT_OPTIONS}
                    onChange={(ev) => {
                        setCardComplete(ev.complete);
                        if (ev.error) setError(ev.error.message);
                        else setError(null);
                    }}
                />
            </Box>

            <CollectPrimaryButton
                type="submit"
                fullWidth
                disabled={disabled || !cardComplete}
                startIcon={
                    processing ? (
                        <CircularProgress size={18} sx={{ color: "#fff" }} />
                    ) : (
                        <LockOutlined sx={{ fontSize: 18 }} />
                    )
                }
            >
                {processing ? "Processing" : submitLabel}
            </CollectPrimaryButton>

            <Typography
                sx={{
                    mt: 1.5,
                    fontFamily: "'Montserrat', sans-serif",
                    fontSize: "0.75rem",
                    color: "#6b7280",
                    textAlign: "center",
                }}
            >
                <LockOutlined
                    sx={{
                        fontSize: 13,
                        verticalAlign: "text-bottom",
                        mr: 0.5,
                        color: colors.mainSecondary,
                    }}
                />
                Payments are securely processed by Stripe. We never store your
                card details.
            </Typography>
        </Box>
    );
};

export default CollectPaymentForm;
